"use client"

import { useCallback, useRef, useState } from "react"

import type { CoviChatItem } from "../model/types"

/**
 * The one thread that is on trial, and its transcript.
 *
 * A thread a PAGE opened — a summary Sofia had something to say about — is not
 * the user starting a conversation, so it is kept only if they answer it.
 * Holding its transcript here rather than in storage is what makes that
 * cheap: an untouched trial is dropped with nothing to clean up, and a reload
 * cannot restore a chat the user never spoke in. See
 * OpenThreadOptions#ephemeral.
 *
 * At most one thread is on trial: a new page's summary retires the last page's
 * untouched thread rather than stacking beside it.
 */
interface EphemeralThread {
  /**
   * The thread on trial, or null. State (not just the ref) so the provider's
   * registry-persist effect re-runs when a trial starts or ends.
   */
  threadId: string | null
  /**
   * Put a thread on trial with its opening transcript. Returns the id of the
   * trial it supersedes, which the caller drops from the registry.
   */
  begin: (threadId: string, items: CoviChatItem[]) => string | null
  /** A trial thread's in-memory transcript; null for any other thread. */
  read: (threadId: string) => CoviChatItem[] | null
  /**
   * Record a thread's transcript. Returns whether it belongs in storage — false
   * only while this thread is still on trial. A user message promotes it: from
   * then on it persists like any other thread, this call included.
   */
  record: (threadId: string, items: CoviChatItem[]) => boolean
  /**
   * End the trial, returning the thread to discard — null when there is none,
   * or when the user already promoted it by speaking in it.
   */
  end: () => string | null
  /** Forget the trial outright. For an org switch, where none of it applies. */
  reset: () => void
}

export function useEphemeralThread(): EphemeralThread {
  const trialRef = useRef<{ id: string; items: CoviChatItem[] } | null>(null)
  const [threadId, setThreadId] = useState<string | null>(null)

  const begin = useCallback((id: string, items: CoviChatItem[]) => {
    const superseded = trialRef.current?.id ?? null
    trialRef.current = { id, items }
    setThreadId(id)
    return superseded === id ? null : superseded
  }, [])

  const read = useCallback(
    (id: string) =>
      trialRef.current?.id === id ? trialRef.current.items : null,
    [],
  )

  const record = useCallback((id: string, items: CoviChatItem[]) => {
    if (trialRef.current?.id !== id) {
      return true
    }
    if (items.some((item) => item.kind === "user")) {
      trialRef.current = null
      setThreadId(null)
      return true
    }
    trialRef.current = { id, items }
    return false
  }, [])

  const end = useCallback(() => {
    const trial = trialRef.current
    if (!trial) {
      return null
    }
    trialRef.current = null
    setThreadId(null)
    return trial.id
  }, [])

  const reset = useCallback(() => {
    trialRef.current = null
    setThreadId(null)
  }, [])

  // Every function here is identity-stable and `threadId` is the only moving
  // part, so callers destructure and depend on the pieces they actually use —
  // never on this object, which is rebuilt each render.
  return { threadId, begin, read, record, end, reset }
}
